async function deriveKey(password, salt) {
  const enc = new TextEncoder();
  const passwordBuffer = enc.encode(password);
  const baseKey = await globalThis.crypto.subtle.importKey(
    "raw",
    passwordBuffer,
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );
  return await globalThis.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt,
      iterations: 1e5,
      // Number of iterations
      hash: "SHA-256"
      // Hash function
    },
    baseKey,
    {
      name: "AES-CBC",
      length: 256
      // Can be 128, 192, or 256 bits
    },
    true,
    // Extractable
    ["encrypt", "decrypt"]
  );
}
function combineArrays(salt, iv, ciphertext) {
  const combined = new Uint8Array(salt.length + iv.length + ciphertext.length);
  combined.set(salt);
  combined.set(iv, salt.length);
  combined.set(ciphertext, salt.length + iv.length);
  return combined;
}
function extractArrays(combined, saltLength, ivLength) {
  if (combined.length < saltLength + ivLength) {
    throw new Error("Combined array too short");
  }
  const salt = combined.slice(0, saltLength);
  const iv = combined.slice(saltLength, saltLength + ivLength);
  const ciphertext = combined.slice(saltLength + ivLength);
  return { salt, iv, ciphertext };
}
export class Uint8ArrayCrypto {
  /**
   * Encrypts a Uint8Array using AES-CBC encryption algorithm with a given password.
   *
   * Output Length: 48 when dataLength < 16 otherwise 48 + dataLength
   *
   * @param {Uint8Array} data - The data to be encrypted.
   * @param {string} password - The password used for encryption.
   * @return {Promise<Uint8Array>} The encrypted data as a Uint8Array.
   */
  static async encrypt(data, password) {
    const salt = globalThis.crypto.getRandomValues(new Uint8Array(16));
    const key = await deriveKey(password, salt);
    const iv = globalThis.crypto.getRandomValues(new Uint8Array(16));
    const encrypted = await globalThis.crypto.subtle.encrypt(
      {
        name: "AES-CBC",
        iv
      },
      key,
      data
    );
    return combineArrays(salt, iv, new Uint8Array(encrypted));
  }
  static async decrypt(encryptedData, password) {
    try {
      const { salt, iv, ciphertext } = extractArrays(encryptedData, 16, 16);
      const key = await deriveKey(password, salt);
      const decrypted = await globalThis.crypto.subtle.decrypt(
        {
          name: "AES-CBC",
          iv
        },
        key,
        ciphertext
      );
      return new Uint8Array(decrypted);
    } catch (e) {
      if (e instanceof DOMException && e.name === "OperationError") {
        throw new Error("Incorrect password");
      }
      throw e;
    }
  }
}
